// frontend/src/widgets/MessagingTemplateLibraryWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-16
// Updated:      2026-09-08 15:56 BST
//
// Description:
//   Library of messaging templates (Email / SMS / WhatsApp).
//   • Shows WhatsApp templates from BOTH:
//       1) DB-backed templates (/api/messaging/templates/whatsapp)
//       2) Twilio/provider templates (/api/messaging/whatsapp/templates)
//   • “Add Template” triggers Creator modal via MessagingPage (page-level modal).
//   • “Sync from Twilio” calls /api/messaging/templates/whatsapp/sync then reloads.
//
// Lifecycle Status:
//   Active.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves one render path for desktop and mobile.
//   - Converts both template tables into labelled cards below 768px.
//   - Retains the existing desktop table presentation at md and above.
//   - Stacks the widget heading and action buttons on narrow screens.
//   - Makes action buttons full-width and touch-friendly on mobile.
//   - Stacks section titles and source labels on mobile.
//   - Allows SIDs, variables, placeholders, preview text and errors to wrap safely.
//   - Uses natural height and visible overflow on mobile while retaining desktop
//     behaviour.
//
// Tenant / Authentication Hardening (2026-09-08):
//   - Removed the hard-coded tenantId = 1 prop.
//   - Removed tenant_id query parameters from authenticated messaging endpoints.
//   - Removed tenantId from the template-creator UI event payload.
//   - Tenant context must be resolved by the authenticated backend session.
//
// Notes:
//   - DO NOT render modals inside widgets (causes clipping).
//   - This widget dispatches an event; MessagingPage owns the modal.
//   - The provider and DB template sources remain independent and are both shown.
//   - A failed DB/provider request remains non-fatal to the other source.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

// If you ever doubt the bundle is updating, search for this string in DevTools Sources.
const BUILD_MARKER__ = "MessagingTemplateLibraryWidget__2026-09-08__mobile-v4";

const MessagingTemplateLibraryWidget = ({ refreshKey = 0 }) => {
  const { theme } = useTheme();

  const [dbTemplates, setDbTemplates] = useState([]);
  const [providerTemplates, setProviderTemplates] = useState([]);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState("");

  const accent =
    theme?.accent_color ||
    "var(--accent-color)" ||
    "#ff7a00";

  // ─────────────────────────────────────────────
  // Helpers (UI formatting)
  // ─────────────────────────────────────────────
  const titleCase = (value) => {
    const str =
      String(value || "").trim();

    if (!str) {
      return "";
    }

    return str
      .split(/\s+/)
      .map((word) =>
        word
          ? word.charAt(0).toUpperCase() +
            word.slice(1).toLowerCase()
          : word
      )
      .join(" ");
  };

  // Use last 3 words from snake_case name, sentence case.
  // Examples:
  //   pca_service_reminder -> Service Reminder
  //   pca_job_status_update -> Job Status Update
  const friendlyFromSnakeLast3 = (name) => {
    const raw =
      String(name || "").trim();

    if (!raw) {
      return "-";
    }

    const parts =
      raw
        .split("_")
        .filter(Boolean);

    if (parts.length === 0) {
      return "-";
    }

    const filtered =
      parts.filter(
        (part, index) => {
          if (
            index === 0 &&
            [
              "pca",
              "whatsapp",
              "template",
            ].includes(
              String(
                part
              ).toLowerCase()
            )
          ) {
            return false;
          }

          return true;
        }
      );

    const useParts =
      (
        filtered.length
          ? filtered
          : parts
      ).slice(-3);

    const sentence =
      useParts.join(" ");

    return (
      titleCase(sentence) ||
      "-"
    );
  };

  // Category: show label, but tooltip shows raw (for debugging).
  // API data can carry label/raw inside provider_payload when top-level fields
  // are absent, so retain that fallback.
  const resolveCategoryLabelAndRaw = (tpl) => {
    const topLabel =
      tpl?.provider_category_label ??
      null;

    const topRaw =
      tpl?.provider_category_raw ??
      null;

    const payload =
      tpl?.provider_payload ||
      null;

    const payloadLabel =
      payload &&
      typeof payload === "object"
        ? payload.provider_category_label ||
          payload.provider_category_label?.toString?.()
        : null;

    const payloadRaw =
      payload &&
      typeof payload === "object"
        ? payload.provider_category_raw ||
          payload.provider_category_raw?.toString?.()
        : null;

    return {
      label:
        topLabel ||
        payloadLabel ||
        null,
      raw:
        topRaw ||
        payloadRaw ||
        null,
    };
  };

  const openCreator = () => {
    window.dispatchEvent(
      new CustomEvent(
        "anpr:whatsappTemplate:create"
      )
    );
  };

  const loadAll = useCallback(
    async () => {
      setLoading(true);
      setErr("");

      const next = {
        db: [],
        provider: [],
      };

      try {
        // 1) DB templates
        try {
          const respDb =
            await axiosInstance.get(
              "/messaging/templates/whatsapp",
              {
                params: {
                  include_inactive:
                    false,
                },
              }
            );

          next.db =
            Array.isArray(
              respDb?.data
            )
              ? respDb.data
              : [];
        } catch (loadError) {
          console.warn(
            "[MessagingTemplateLibraryWidget] DB list failed",
            loadError
          );
        }

        // 2) Provider templates (Twilio list)
        try {
          const respProvider =
            await axiosInstance.get(
              "/messaging/whatsapp/templates"
            );

          next.provider =
            Array.isArray(
              respProvider?.data
            )
              ? respProvider.data
              : [];
        } catch (loadError) {
          console.warn(
            "[MessagingTemplateLibraryWidget] Provider list failed",
            loadError
          );
        }

        setDbTemplates(
          next.db
        );

        setProviderTemplates(
          next.provider
        );

        console.log(
          "[MessagingTemplateLibraryWidget] loaded",
          {
            dbCount:
              next.db.length,
            providerCount:
              next.provider.length,
            BUILD_MARKER__,
          }
        );
      } catch (loadError) {
        console.error(
          "[MessagingTemplateLibraryWidget] loadAll error",
          loadError
        );

        setErr(
          "Failed to load templates"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const syncFromTwilio =
    async () => {
      setSyncing(true);
      setErr("");

      try {
        const resp =
          await axiosInstance.post(
            "/messaging/templates/whatsapp/sync"
          );

        console.log(
          "[MessagingTemplateLibraryWidget] sync result",
          resp?.data
        );

        await loadAll();
      } catch (syncError) {
        console.error(
          "[MessagingTemplateLibraryWidget] sync error",
          syncError
        );

        const msg =
          syncError?.response?.data?.detail ||
          syncError?.response?.data?.message ||
          "Sync failed. Check backend logs.";

        setErr(msg);
      } finally {
        setSyncing(false);
      }
    };

  useEffect(() => {
    loadAll();
  }, [
    loadAll,
    refreshKey,
  ]);

  const providerBySid = useMemo(
    () => {
      const map =
        new Map();

      (
        providerTemplates ||
        []
      ).forEach((template) => {
        const sid =
          template?.id ||
          template?.template_id;

        if (sid) {
          map.set(
            String(sid),
            template
          );
        }
      });

      return map;
    },
    [
      providerTemplates,
    ]
  );

  const renderMobileLabel = (label) => (
    <span className="mr-3 shrink-0 font-semibold text-gray-500 md:hidden">
      {label}
    </span>
  );

  const renderDbTable = () => {
    if (
      dbTemplates.length === 0
    ) {
      return (
        <div className="break-words italic text-gray-600">
          No templates stored in DB yet (whatsapp_templates is empty).
        </div>
      );
    }

    return (
      <table className="block w-full text-left text-sm text-gray-700 md:table md:rounded md:border">
        <thead className="hidden border-b bg-gray-100 md:table-header-group">
          <tr>
            <th className="px-3 py-2">
              Name
            </th>

            <th className="px-3 py-2">
              Content SID
            </th>

            <th className="px-3 py-2">
              Language
            </th>

            <th className="px-3 py-2">
              Category
            </th>

            <th className="px-3 py-2">
              Provider Status
            </th>

            <th className="px-3 py-2">
              Variables
            </th>
          </tr>
        </thead>

        <tbody className="block space-y-3 md:table-row-group md:space-y-0">
          {dbTemplates.map(
            (tpl) => {
              const vars =
                Array.isArray(
                  tpl.variables_schema
                )
                  ? tpl.variables_schema
                  : [];

              const provider =
                providerBySid.get(
                  String(
                    tpl.content_sid ||
                      ""
                  )
                );

              const providerStatus =
                provider?.status ||
                provider?.provider_status ||
                null;

              const {
                label: catLabel,
                raw: catRaw,
              } =
                resolveCategoryLabelAndRaw(
                  tpl
                );

              const categoryDisplay =
                catLabel ||
                "-";

              const categoryTooltip =
                catRaw
                  ? `Raw: ${catRaw}`
                  : catLabel
                    ? `Label: ${catLabel}`
                    : "No provider category found (check sync payload)";

              const variableDisplay =
                vars.length === 0
                  ? "-"
                  : vars
                      .map(
                        (variable) =>
                          variable.label ||
                          variable.key
                      )
                      .slice(0, 4)
                      .join(", ");

              return (
                <tr
                  key={tpl.id}
                  className="block overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:table-row md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:shadow-none md:hover:bg-gray-50"
                >
                  <td className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2">
                    {renderMobileLabel(
                      "Name"
                    )}

                    <span
                      className="min-w-0 flex-1 break-words text-right md:text-left"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {friendlyFromSnakeLast3(
                        tpl.name
                      )}
                    </span>
                  </td>

                  <td
                    className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2"
                    style={{
                      fontFamily:
                        "monospace",
                      fontSize:
                        12,
                    }}
                  >
                    {renderMobileLabel(
                      "Content SID"
                    )}

                    <span
                      className="min-w-0 flex-1 break-all text-right md:text-left"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {tpl.content_sid ||
                        "-"}
                    </span>
                  </td>

                  <td className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2">
                    {renderMobileLabel(
                      "Language"
                    )}

                    <span className="min-w-0 flex-1 break-words text-right md:text-left">
                      {tpl.language ||
                        tpl
                          ?.provider_payload
                          ?.provider_language ||
                        "-"}
                    </span>
                  </td>

                  <td
                    className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2"
                    title={
                      categoryTooltip
                    }
                  >
                    {renderMobileLabel(
                      "Category"
                    )}

                    <span className="min-w-0 flex-1 break-words text-right md:text-left">
                      {categoryDisplay}
                    </span>
                  </td>

                  <td className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2">
                    {renderMobileLabel(
                      "Provider Status"
                    )}

                    <span className="min-w-0 flex-1 break-words text-right md:text-left">
                      {providerStatus ||
                        tpl.provider_status ||
                        tpl.status ||
                        "-"}
                    </span>
                  </td>

                  <td className="flex min-w-0 items-start justify-between gap-4 px-3 py-3 md:table-cell md:py-2">
                    {renderMobileLabel(
                      "Variables"
                    )}

                    <span
                      className="min-w-0 flex-1 break-words text-right md:text-left"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {variableDisplay}
                      {vars.length > 4
                        ? ` +${
                            vars.length -
                            4
                          }`
                        : ""}
                    </span>
                  </td>
                </tr>
              );
            }
          )}
        </tbody>
      </table>
    );
  };

  const renderProviderTable = () => {
    if (
      providerTemplates.length ===
      0
    ) {
      return (
        <div className="break-words italic text-gray-600">
          No templates returned from provider endpoint.
        </div>
      );
    }

    return (
      <table className="block w-full text-left text-sm text-gray-700 md:table md:rounded md:border">
        <thead className="hidden border-b bg-gray-100 md:table-header-group">
          <tr>
            <th className="px-3 py-2">
              Name
            </th>

            <th className="px-3 py-2">
              Template SID
            </th>

            <th className="px-3 py-2">
              Category
            </th>

            <th className="px-3 py-2">
              Channel
            </th>

            <th className="px-3 py-2">
              Placeholders
            </th>

            <th className="px-3 py-2">
              Preview
            </th>
          </tr>
        </thead>

        <tbody className="block space-y-3 md:table-row-group md:space-y-0">
          {providerTemplates.map(
            (template) => {
              const sid =
                template?.id ||
                template?.template_id ||
                "-";

              const placeholders =
                Array.isArray(
                  template?.placeholders
                )
                  ? template.placeholders
                  : [];

              return (
                <tr
                  key={String(sid)}
                  className="block overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:table-row md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:shadow-none md:hover:bg-gray-50 md:align-top"
                >
                  <td className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2">
                    {renderMobileLabel(
                      "Name"
                    )}

                    <span
                      className="min-w-0 flex-1 break-words text-right md:text-left"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {template?.friendly_name ||
                        template?.name ||
                        "-"}
                    </span>
                  </td>

                  <td
                    className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2"
                    style={{
                      fontFamily:
                        "monospace",
                      fontSize:
                        12,
                    }}
                  >
                    {renderMobileLabel(
                      "Template SID"
                    )}

                    <span
                      className="min-w-0 flex-1 break-all text-right md:text-left"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {sid}
                    </span>
                  </td>

                  <td className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2">
                    {renderMobileLabel(
                      "Category"
                    )}

                    <span className="min-w-0 flex-1 break-words text-right md:text-left">
                      {template?.category ||
                        "-"}
                    </span>
                  </td>

                  <td className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2">
                    {renderMobileLabel(
                      "Channel"
                    )}

                    <span className="min-w-0 flex-1 break-words text-right md:text-left">
                      {template?.channel ||
                        "whatsapp"}
                    </span>
                  </td>

                  <td className="flex min-w-0 items-start justify-between gap-4 border-b border-gray-100 px-3 py-3 md:table-cell md:border-b-0 md:py-2">
                    {renderMobileLabel(
                      "Placeholders"
                    )}

                    <span
                      className="min-w-0 flex-1 break-words text-right md:text-left"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {placeholders.length
                        ? placeholders.join(
                            ", "
                          )
                        : "-"}
                    </span>
                  </td>

                  <td className="flex min-w-0 items-start justify-between gap-4 px-3 py-3 md:table-cell md:py-2">
                    {renderMobileLabel(
                      "Preview"
                    )}

                    <span
                      className="min-w-0 flex-1 whitespace-pre-wrap break-words text-right md:text-left"
                      style={{
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {template?.preview ||
                        "-"}
                    </span>
                  </td>
                </tr>
              );
            }
          )}
        </tbody>
      </table>
    );
  };

  return (
    <div className="h-auto min-h-0 w-full overflow-visible p-3 md:p-4">
      <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h3 className="m-0 break-words text-xl font-bold">
          WhatsApp Templates
        </h3>

        <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto md:flex-wrap">
          <button
            type="button"
            onClick={
              syncFromTwilio
            }
            disabled={
              syncing
            }
            className="min-h-[44px] w-full rounded bg-btnGeneralNormal px-4 py-2 font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick disabled:cursor-default sm:w-auto"
            style={{
              opacity:
                syncing
                  ? 0.7
                  : 1,
            }}
            title="Pull templates from Twilio into the DB table (whatsapp_templates)"
          >
            {syncing
              ? "Syncing…"
              : "Sync from Twilio"}
          </button>

          <button
            type="button"
            onClick={
              openCreator
            }
            className="min-h-[44px] w-full rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionClick sm:w-auto"
            style={{
              backgroundColor:
                theme
                  ?.button_action
                  ?.normal ||
                accent,
            }}
          >
            + Add Template
          </button>
        </div>
      </div>

      {err && (
        <div
          role="alert"
          className="mb-2 break-words rounded border border-red-300 bg-red-50 p-2 text-sm text-red-600"
          style={{
            overflowWrap:
              "anywhere",
          }}
        >
          {err}
        </div>
      )}

      {loading ? (
        <div
          className="italic text-gray-500"
          aria-live="polite"
        >
          Loading templates…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3.5">
          {/* DB Section */}
          <section className="min-w-0 rounded-[10px] border border-[#e6e6e6] bg-white p-3">
            <div className="flex min-w-0 flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-2.5">
              <div className="min-w-0 break-words text-sm font-extrabold">
                Stored Templates (DB)
                <span className="ml-2 text-xs font-bold text-gray-500">
                  {dbTemplates.length}
                </span>
              </div>

              <div
                className="min-w-0 break-words text-xs text-gray-500"
                style={{
                  overflowWrap:
                    "anywhere",
                }}
              >
                Source:{" "}
                <span className="font-mono break-all">
                  /api/messaging/templates/whatsapp
                </span>
              </div>
            </div>

            <div className="mt-2.5 min-w-0">
              {renderDbTable()}
            </div>
          </section>

          {/* Provider Section */}
          <section
            className="min-w-0 rounded-[10px] border p-3"
            style={{
              borderColor:
                accent,
              background:
                "rgba(255,122,0,0.03)",
            }}
          >
            <div className="flex min-w-0 flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-2.5">
              <div
                className="min-w-0 break-words text-sm font-extrabold"
                style={{
                  color:
                    accent,
                }}
              >
                Provider Templates (Twilio)
                <span className="ml-2 text-xs font-bold text-gray-500">
                  {providerTemplates.length}
                </span>
              </div>

              <div
                className="min-w-0 break-words text-xs text-gray-500"
                style={{
                  overflowWrap:
                    "anywhere",
                }}
              >
                Source:{" "}
                <span className="font-mono break-all">
                  /api/messaging/whatsapp/templates
                </span>
              </div>
            </div>

            <div className="mt-2.5 min-w-0">
              {renderProviderTable()}
            </div>
          </section>

          {/* Tiny debug footer */}
          <div
            className="min-w-0 break-words text-[11px] text-gray-400"
            style={{
              overflowWrap:
                "anywhere",
            }}
          >
            Build:{" "}
            <span className="font-mono break-all">
              {BUILD_MARKER__}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagingTemplateLibraryWidget;
